import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    List,
    ListItemText,
    CircularProgress,
    ListItemButton
} from '@mui/material';
import api from '../api';

interface SaddleAssignmentDialogProps {
    open: boolean;
    onClose: () => void;
    onAssignSuccess: () => void;
    saleItemId: number;
}

interface Saddle {
    id: number;
    upper_radius: number;
    lower_radius: number;
    width: number;
    height: number;
    site?: string;
    item?: string;
    company_name?: string;
    site_name?: string;
}

const SaddleAssignmentDialog: React.FC<SaddleAssignmentDialogProps> = ({
    open,
    onClose,
    onAssignSuccess,
    saleItemId
}) => {
    const [materialType, setMaterialType] = useState<'new' | 'reuse'>('new');
    const [saddleData, setSaddleData] = useState({
        upper_radius: '',
        lower_radius: '',
        width: '',
        height: ''
    });
    const [reuseSaddles, setReuseSaddles] = useState<Saddle[]>([]);
    const [selectedReuseSaddleId, setSelectedReuseSaddleId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (materialType === 'reuse') {
            fetchReuseSaddles();
        }
    }, [materialType]);

    const fetchReuseSaddles = async () => {
        setLoading(true);
        try {
            const response = await api.get('/api/saddles/available');
            setReuseSaddles(response.data);
        } catch (error) {
            console.error('재활용 새들 목록 조회 오류:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectSaddle = (saddle: Saddle) => {
        setSaddleData({
            upper_radius: saddle.upper_radius.toString(),
            lower_radius: saddle.lower_radius.toString(),
            width: saddle.width.toString(),
            height: saddle.height.toString()
        });
        setSelectedReuseSaddleId(saddle.id);
    };

    const handleAssign = async () => {
        try {
            if (materialType === 'new') {
                await api.post(`/api/sale-items/${saleItemId}/saddles`, {
                    ...saddleData,
                    type: 'new',
                    saddle_id: null
                });
            } else if (materialType === 'reuse' && selectedReuseSaddleId !== null) {
                await api.post(`/api/sale-items/${saleItemId}/saddles`, {
                    type: 'reuse',
                    saddle_id: selectedReuseSaddleId
                });
            }
            onAssignSuccess();
        } catch (error) {
            console.error('새들 자재 할당 오류:', error);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>새들 자재 할당</DialogTitle>
            <DialogContent>
                <FormControl fullWidth margin="normal">
                    <InputLabel>자재 타입</InputLabel>
                    <Select
                        value={materialType}
                        onChange={(e) => {
                            setMaterialType(e.target.value as 'new' | 'reuse');
                            setSelectedReuseSaddleId(null);
                            setSaddleData({ upper_radius: '', lower_radius: '', width: '', height: '' });
                        }}
                        label="자재 타입"
                    >
                        <MenuItem value="new">신규</MenuItem>
                        <MenuItem value="reuse">재활용</MenuItem>
                    </Select>
                </FormControl>

                {(materialType === 'new' || selectedReuseSaddleId !== null) && (
                    <>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="상부 R값"
                            type="number"
                            value={saddleData.upper_radius}
                            onChange={(e) => setSaddleData({ ...saddleData, upper_radius: e.target.value })}
                        />
                        <TextField
                            fullWidth
                            margin="normal"
                            label="하부 R값"
                            type="number"
                            value={saddleData.lower_radius}
                            onChange={(e) => setSaddleData({ ...saddleData, lower_radius: e.target.value })}
                        />
                        <TextField
                            fullWidth
                            margin="normal"
                            label="너비"
                            type="number"
                            value={saddleData.width}
                            onChange={(e) => setSaddleData({ ...saddleData, width: e.target.value })}
                        />
                        <TextField
                            fullWidth
                            margin="normal"
                            label="높이"
                            type="number"
                            value={saddleData.height}
                            onChange={(e) => setSaddleData({ ...saddleData, height: e.target.value })}
                        />
                    </>
                )}

                {materialType === 'reuse' && selectedReuseSaddleId === null && (
                    <>
                        {loading ? (
                            <CircularProgress />
                        ) : (
                            <List>
                                {reuseSaddles.map((saddle) => (
                                    <ListItemButton
                                        key={saddle.id}
                                        onClick={() => handleSelectSaddle(saddle)}
                                    >
                                        <ListItemText
                                            primary={`[${saddle.company_name} / ${saddle.site_name} / ${saddle.item}] 상부 R: ${saddle.upper_radius}, 하부 R: ${saddle.lower_radius}, 너비: ${saddle.width}, 높이: ${saddle.height}`}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>취소</Button>
                <Button onClick={handleAssign} variant="contained" color="primary">
                    할당
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SaddleAssignmentDialog;
